import { useState, useCallback, useRef } from 'react';
import type {
  RoleDefinition,
  AgentState,
  CandidateResult,
} from '../../types/api';
import { AGENT_CONFIGS, AGENT_STAGGER_MS } from '../../config/agents';
import { DEFAULT_ROLE, DEFAULT_WEIGHTS } from '../../config/presets';
import { CANDIDATES } from '../../config/candidates';
import { fetchEndpoint } from '../../lib/api-client';
import { requestAnalysis } from '../../lib/analysis-client';
import { simulateATS } from '../../lib/ats-simulator';
import RoleForm from './RoleForm';
import AgentDashboard from './AgentDashboard';
import CandidateGrid from './CandidateGrid';
import ATSComparison from './ATSComparison';

type Stage = 'form' | 'discovery' | 'results';

function initAgents(): AgentState[] {
  return AGENT_CONFIGS.map((config) => ({
    ...config,
    status: 'waiting' as const,
    data: null,
    dataSnippets: [],
  }));
}

/** Gather all endpoint data for a single candidate's API */
async function gatherCandidateData(apiBase: string): Promise<Record<string, unknown>> {
  const collectedData: Record<string, unknown> = {};
  for (const agentConfig of AGENT_CONFIGS.slice(0, 3)) {
    for (const endpoint of agentConfig.endpoints) {
      try {
        const result = await fetchEndpoint(endpoint, apiBase);
        collectedData[endpoint] = result.data;
      } catch (err) {
        console.error(`Failed ${endpoint} for ${apiBase}:`, err);
      }
    }
  }
  return collectedData;
}

export default function CompanyView() {
  const [stage, setStage] = useState<Stage>('form');
  const [role, setRole] = useState<RoleDefinition>({ ...DEFAULT_ROLE, weights: { ...DEFAULT_WEIGHTS } });
  const [agents, setAgents] = useState<AgentState[]>(initAgents);
  const [candidateResults, setCandidateResults] = useState<Record<string, CandidateResult>>({});
  const [selectedCandidateId, setSelectedCandidateId] = useState('ted-lango');

  // Use ref for agents during orchestration to avoid stale closures
  const agentsRef = useRef(agents);
  agentsRef.current = agents;

  const updateAgent = useCallback(
    (index: number, updates: Partial<AgentState>) => {
      setAgents((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], ...updates };
        return next;
      });
    },
    [],
  );

  const addSnippet = useCallback(
    (index: number, snippet: string) => {
      setAgents((prev) => {
        const next = [...prev];
        next[index] = {
          ...next[index],
          dataSnippets: [...next[index].dataSnippets, snippet],
        };
        return next;
      });
    },
    [],
  );

  /**
   * Runs the visual gather agent animation. Fetches from the first candidate's
   * API for the visual data snippets, while data for all candidates is gathered
   * in parallel behind the scenes.
   */
  const runGatherAgentVisual = useCallback(
    async (agentIndex: number): Promise<void> => {
      const agent = AGENT_CONFIGS[agentIndex];
      const startTime = Date.now();

      updateAgent(agentIndex, { status: 'searching', startTime });

      // Show snippets from first candidate's API for the visual animation
      const primaryBase = CANDIDATES[0].apiBase;
      for (const endpoint of agent.endpoints) {
        try {
          const result = await fetchEndpoint(endpoint, primaryBase);
          const snippet = JSON.stringify(result.data, null, 2);
          const truncated =
            snippet.length > 200
              ? snippet.slice(0, 200) + '\n  ...'
              : snippet;
          addSnippet(agentIndex, truncated);
        } catch (err) {
          console.error(`Agent ${agent.id} visual fetch failed on ${endpoint}:`, err);
          addSnippet(agentIndex, `// Error fetching ${endpoint}`);
        }
      }

      const endTime = Date.now();
      updateAgent(agentIndex, {
        status: 'found',
        endTime,
      });
    },
    [updateAgent, addSnippet],
  );

  const handleLaunch = useCallback(async () => {
    // Reset state
    setAgents(initAgents());
    setCandidateResults({});
    setSelectedCandidateId('ted-lango');
    setStage('discovery');

    // Phase 1: Run the 3 gather agent animations with staggering (visual only)
    const visualPromises: Promise<void>[] = [];
    for (let i = 0; i < 3; i++) {
      if (i > 0) {
        await new Promise((resolve) => setTimeout(resolve, AGENT_STAGGER_MS));
      }
      visualPromises.push(runGatherAgentVisual(i));
    }

    // In parallel, gather ALL candidate data behind the scenes
    const candidateDataPromises = CANDIDATES.map(async (candidate) => {
      const data = await gatherCandidateData(candidate.apiBase);
      const [analysis, atsResult] = await Promise.all([
        requestAnalysis(role, data, candidate.id).catch((err) => {
          console.error(`Analysis failed for ${candidate.id}, using fallback:`, err);
          return null;
        }),
        Promise.resolve(simulateATS(role, JSON.stringify(data))),
      ]);
      return { candidateId: candidate.id, candidateData: data, analysis, atsResult } as CandidateResult;
    });

    // Wait for visual animation to complete
    await Promise.all(visualPromises);

    // Phase 2: Synthesis agent animation
    const synthesisStart = Date.now();
    updateAgent(3, { status: 'searching', startTime: synthesisStart });

    // Wait for all candidate data
    const allResults = await Promise.all(candidateDataPromises);

    // Handle fallbacks for any failed analyses
    for (const result of allResults) {
      if (result.analysis === null) {
        try {
          const { generateDynamicFallback } = await import('../../lib/dynamic-fallback');
          result.analysis = generateDynamicFallback(role);
        } catch {
          // If even fallback fails, leave as null
        }
      }
    }

    // Build results map
    const resultsMap: Record<string, CandidateResult> = {};
    for (const result of allResults) {
      resultsMap[result.candidateId] = result;
    }

    const synthesisEnd = Date.now();
    updateAgent(3, {
      status: 'analyzed',
      endTime: synthesisEnd,
      data: { candidates_analyzed: allResults.length },
      dataSnippets: [
        `// Analyzed ${allResults.length} candidates\n` +
        allResults.map((r) => `// ${r.candidateId}: score ${r.analysis?.match_score ?? 'N/A'}`).join('\n'),
      ],
    });

    setCandidateResults(resultsMap);

    // Brief pause before transitioning to results
    await new Promise((resolve) => setTimeout(resolve, 1500));
    setStage('results');
  }, [role, runGatherAgentVisual, updateAgent]);

  const handleBack = () => {
    setStage('form');
    setAgents(initAgents());
  };

  const selectedResult = candidateResults[selectedCandidateId];
  const selectedCandidate = CANDIDATES.find((c) => c.id === selectedCandidateId);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
      {/* Back button */}
      {stage !== 'form' && (
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-sm text-ti-text-muted hover:text-ti-text transition-colors mb-6 cursor-pointer group"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            className="group-hover:-translate-x-0.5 transition-transform"
          >
            <path
              d="M10 3L5 8L10 13"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Back to Role Form
        </button>
      )}

      {/* Stage rendering */}
      {stage === 'form' && (
        <RoleForm role={role} onChange={setRole} onLaunch={handleLaunch} />
      )}

      {stage === 'discovery' && (
        <AgentDashboard agents={agents} stage="discovery" />
      )}

      {stage === 'results' && (
        <div className="space-y-8">
          {/* Show completed dashboard in compact form */}
          <AgentDashboard agents={agents} stage="results" />

          {/* Divider */}
          <div className="border-t border-ti-border" />

          {/* Candidate selection grid */}
          <CandidateGrid
            candidates={CANDIDATES.map((profile) => ({
              profile,
              analysis: candidateResults[profile.id]?.analysis ?? null,
              atsResult: candidateResults[profile.id]?.atsResult ?? null,
            }))}
            selectedId={selectedCandidateId}
            onSelect={setSelectedCandidateId}
          />

          {/* Selected candidate's detailed results */}
          {selectedResult?.analysis && selectedResult?.atsResult && (
            <>
              <div className="border-t border-ti-border" />
              <ATSComparison
                analysis={selectedResult.analysis}
                atsResult={selectedResult.atsResult}
                agents={agents}
                candidateName={selectedCandidate?.name ?? ''}
                roleTitle={role.title}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
